// CHA-626 (W7) — Directory: a tenant's PEOPLE and GROUPS. Platform, tenant-agnostic.
//
// Not access control: this is who the tenant works with (employees, vendor contacts, brokers), whether
// or not they ever sign in. Roles here are job-function TAGS (buyer, merchandiser…), never permissions —
// the ACL stays on the Roles & Users screen. A person who can be a recipient must have an email.
//
// The shell passes `call(payload)`, which runs a gateway action for its tenant and resolves to the
// callable's data. Reading and writing are admin-only on the server, so the shell must mount this panel
// for admins only (W7b). Every write goes through upsert_person / upsert_group, which validate the fields,
// check group members exist in this tenant, and enforce the admin role. Nothing here sends mail (W8).
import React, { useCallback, useRef, useState } from 'react';

const inputCls = 'w-full border border-carbon-border bg-carbon-white px-2 py-1 text-[12px] text-carbon-ink';
const labelCls = 'mb-0.5 block font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-60';
const btnCls = 'font-plex-mono text-[10px] uppercase tracking-[0.08em] text-carbon-gray-70 hover:text-carbon-ink disabled:opacity-40';
const thCls = 'py-1 font-normal';

// "a, b ,a" -> ['a', 'b'] (the server checks each entry is a tag).
const toList = (text) => [...new Set(String(text || '').split(',').map((s) => s.trim()).filter(Boolean))];

const EMPTY_PERSON = { id: '', name: '', email: '', uid: '', recipient: false, roles: '' };
const EMPTY_GROUP = { id: '', name: '', memberIds: [] };

export default function DirectoryPanel({ call }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [people, setPeople] = useState([]);
  const [groups, setGroups] = useState([]);
  const [personForm, setPersonForm] = useState(null);
  const [groupForm, setGroupForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Every list_directory request gets a sequence number; only the LATEST one may set the lists, the
  // error, or clear `loading`. A slower, older response (hide/show quickly, or save-then-reload while a
  // list is in flight) is dropped instead of overwriting newer state (CodeRabbit on #463).
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++loadSeq.current;
    setLoading(true);
    setError(null);
    try {
      const res = await call({ action: 'list_directory' });
      if (seq !== loadSeq.current) return;
      setPeople(Array.isArray(res?.people) ? res.people : []);
      setGroups(Array.isArray(res?.groups) ? res.groups : []);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      setError(err?.message || 'Could not load the directory.');
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [call]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) load();
    else { setPersonForm(null); setGroupForm(null); }
  };

  const save = async (payload, close) => {
    setSaving(true);
    setError(null);
    try {
      await call(payload);
      close();
      await load();
    } catch (err) {
      setError(err?.message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const savePerson = (e) => {
    e.preventDefault();
    const f = personForm;
    save({
      action: 'upsert_person',
      // Add is create-only (an existing id is refused, never overwritten); Edit updates.
      ...(f.editing ? {} : { create: true }),
      person: {
        id: f.id,
        name: f.name,
        email: f.email.trim() || null,
        uid: f.uid.trim() || null,
        recipient: f.recipient,
        roles: toList(f.roles),
      },
    }, () => setPersonForm(null));
  };

  const saveGroup = (e) => {
    e.preventDefault();
    const f = groupForm;
    save({ action: 'upsert_group', ...(f.editing ? {} : { create: true }), group: { id: f.id, name: f.name, memberIds: f.memberIds } }, () => setGroupForm(null));
  };

  const editPerson = (p) => { setGroupForm(null); setPersonForm({ id: p.id, name: p.name || '', email: p.email || '', uid: p.uid || '', recipient: p.recipient === true, roles: (p.roles || []).join(', '), editing: true }); };
  const editGroup = (g) => { setPersonForm(null); setGroupForm({ id: g.id, name: g.name || '', memberIds: [...(g.memberIds || [])], editing: true }); };

  // Editing a field clears the last save error: the user is fixing it (CodeRabbit nit on #463).
  const pField = (key) => ({ value: personForm[key], onChange: (ev) => { setError(null); setPersonForm((f) => ({ ...f, [key]: ev.target.value })); } });
  const gField = (key) => ({ value: groupForm[key], onChange: (ev) => { setError(null); setGroupForm((f) => ({ ...f, [key]: ev.target.value })); } });
  const nameOf = (id) => (people.find((p) => p.id === id) || {}).name || id;

  return (
    <section className="border border-carbon-border bg-carbon-white" data-testid="directory-panel" aria-label="Directory">
      <div className="flex items-center justify-between border-b border-carbon-border bg-carbon-gray-10 px-4 py-2">
        <span className="font-plex-mono text-[11px] uppercase tracking-[0.12em] text-carbon-gray-60">
          Directory{open ? ` · ${people.length} people · ${groups.length} groups` : ''}
        </span>
        <button type="button" onClick={toggle} className={btnCls}>{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <div className="space-y-4 px-4 py-3">
          <p className="text-[11px] text-carbon-gray-60">
            People the business works with. Roles here describe the job (buyer, vendor contact), not app permissions.
          </p>
          {error && <div role="alert" className="text-sm text-carbon-red-60">{error}</div>}
          {loading && <div className="text-sm text-carbon-gray-60">Loading the directory…</div>}

          {!loading && (
            <div className="space-y-2">
              <div className="font-plex-mono text-[10px] uppercase tracking-[0.1em] text-carbon-gray-60">People</div>
              {people.length === 0 && <div className="text-sm text-carbon-gray-60">No people yet.</div>}
              {people.length > 0 && (
                <table className="w-full text-left text-[12px]" data-testid="directory-people">
                  <thead><tr className="text-carbon-gray-60"><th className={thCls}>Name</th><th className={thCls}>Email</th><th className={thCls}>Roles</th><th className={thCls}>Recipient</th><th className={thCls}>App user</th><th /></tr></thead>
                  <tbody>
                    {people.map((p) => (
                      <tr key={p.id} data-testid={`person-${p.id}`} className="border-t border-carbon-border">
                        <td className="py-1 text-carbon-ink">{p.name}</td>
                        <td className="font-plex-mono">{p.email || '—'}</td>
                        <td className="font-plex-mono">{(p.roles || []).join(', ') || '—'}</td>
                        <td>{p.recipient ? 'Yes' : 'No'}</td>
                        <td>{p.uid ? 'Yes' : 'No'}</td>
                        <td className="text-right"><button type="button" className={btnCls} onClick={() => editPerson(p)} disabled={saving}>Edit</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!personForm && <button type="button" className={btnCls} onClick={() => { setGroupForm(null); setPersonForm({ ...EMPTY_PERSON, editing: false }); }} disabled={saving}>Add person</button>}
              {personForm && (
                <form onSubmit={savePerson} className="grid grid-cols-1 gap-2 border border-carbon-border p-3 sm:grid-cols-2" data-testid="person-form">
                  <label><span className={labelCls}>Id</span><input className={inputCls} aria-label="Person id" {...pField('id')} disabled={personForm.editing} required /></label>
                  <label><span className={labelCls}>Name</span><input className={inputCls} aria-label="Person name" {...pField('name')} required /></label>
                  <label><span className={labelCls}>Email</span><input className={inputCls} aria-label="Email" type="email" {...pField('email')} required={personForm.recipient} /></label>
                  <label><span className={labelCls}>Roles (comma-separated)</span><input className={inputCls} aria-label="Roles" {...pField('roles')} /></label>
                  <label><span className={labelCls}>App user id (optional)</span><input className={inputCls} aria-label="App user id" {...pField('uid')} /></label>
                  <label className="flex items-center gap-2 pt-4 text-[12px]">
                    <input type="checkbox" aria-label="Can receive workflow email" checked={personForm.recipient}
                      onChange={(ev) => { setError(null); setPersonForm((f) => ({ ...f, recipient: ev.target.checked })); }} />
                    Can receive workflow email (needs an email)
                  </label>
                  <div className="flex gap-4 sm:col-span-2">
                    <button type="submit" className={btnCls} disabled={saving}>{saving ? 'Saving…' : 'Save person'}</button>
                    <button type="button" className={btnCls} onClick={() => setPersonForm(null)} disabled={saving}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          )}

          {!loading && (
            <div className="space-y-2">
              <div className="font-plex-mono text-[10px] uppercase tracking-[0.1em] text-carbon-gray-60">Groups</div>
              {groups.length === 0 && <div className="text-sm text-carbon-gray-60">No groups yet.</div>}
              {groups.length > 0 && (
                <table className="w-full text-left text-[12px]" data-testid="directory-groups">
                  <thead><tr className="text-carbon-gray-60"><th className={thCls}>Group</th><th className={thCls}>Members</th><th /></tr></thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr key={g.id} data-testid={`group-${g.id}`} className="border-t border-carbon-border">
                        <td className="py-1 text-carbon-ink">{g.name}</td>
                        <td>{(g.memberIds || []).map(nameOf).join(', ') || '—'}</td>
                        <td className="text-right"><button type="button" className={btnCls} onClick={() => editGroup(g)} disabled={saving}>Edit</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {!groupForm && <button type="button" className={btnCls} onClick={() => { setPersonForm(null); setGroupForm({ ...EMPTY_GROUP, editing: false }); }} disabled={saving}>Add group</button>}
              {groupForm && (
                <form onSubmit={saveGroup} className="grid grid-cols-1 gap-2 border border-carbon-border p-3 sm:grid-cols-2" data-testid="group-form">
                  <label><span className={labelCls}>Id</span><input className={inputCls} aria-label="Group id" {...gField('id')} disabled={groupForm.editing} required /></label>
                  <label><span className={labelCls}>Name</span><input className={inputCls} aria-label="Group name" {...gField('name')} required /></label>
                  <fieldset className="sm:col-span-2">
                    <legend className={labelCls}>Members</legend>
                    {people.length === 0 && <div className="text-[12px] text-carbon-gray-60">Add people first.</div>}
                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {people.map((p) => (
                        <label key={p.id} className="flex items-center gap-1 text-[12px]">
                          <input type="checkbox" aria-label={`Member ${p.name}`} checked={groupForm.memberIds.includes(p.id)}
                            onChange={(ev) => { setError(null); setGroupForm((f) => ({
                              ...f,
                              memberIds: ev.target.checked ? [...f.memberIds, p.id] : f.memberIds.filter((m) => m !== p.id),
                            })); }} />
                          {p.name}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="flex gap-4 sm:col-span-2">
                    <button type="submit" className={btnCls} disabled={saving}>{saving ? 'Saving…' : 'Save group'}</button>
                    <button type="button" className={btnCls} onClick={() => setGroupForm(null)} disabled={saving}>Cancel</button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
